// lab/src/component/HomePage/CategorySection/PackagePage.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { 
  Heart, Star, Clock, 
  ShoppingBag, Package as PackageIcon, Droplet, 
  Activity, Wind, Shield,
  X, Loader2
} from 'lucide-react';
import GetPackageDetails from '../LabTest/GetPackageDetails';
import ReplaceCartPopup from '../LabTest/ReplaceCartPopup';
import { usePackageCartActions } from '../../hooks/usePackageCartActions';
import { useCartActions } from '../../hooks/useCartActions'; // ✅ ADDED
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from '../../Redux/wishlistSlice';
import { fetchAllRatings } from '../../Redux/ratingSlice';
import Coupon from '../Coupon/Coupon'; // ✅ ADDED
import LoginPopup from '../common/LoginPopup'; // ✅ ADDED

// ✅ Import from API file
import {
  getUserId,
  getTestsCount,
} from '../../services/HomePage/AllPackagesAPI';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://online-tech.in/api').replace(/\/+$/, '');

export default function PackagePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const cart = useSelector((state) => state.cart.items || []);
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);
  
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false); // ✅ ADDED

  const { addPackage, removePackage, hasDifferentStore } = usePackageCartActions();
  
  // ✅ Use useCartActions for login popup
  const { 
    addWithReplaceCheck, 
    removeItem, 
    LoginPopupComponent  // ✅ This will handle login popup automatically
  } = useCartActions();

  // ✅ Load Wishlist
  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  // ✅ Check if in wishlist
  const isInWishlist = (productId, categoryId = 3) => {
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  // ✅ Toggle Wishlist
  const toggleWishlist = async (pkg, e) => {
    e.stopPropagation();
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }

    const productId = pkg.packageId;
    const categoryId = 3;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      alert('Failed to update wishlist. Please try again.');
    }
  };

  // ✅ Get Package Icon
  const getPackageIcon = (name) => {
    if (!name) return <PackageIcon size={24} />;
    const nameLower = name.toLowerCase();
    if (nameLower.includes('diabetes')) return <Droplet size={24} />;
    if (nameLower.includes('heart') || nameLower.includes('cardiac')) return <Activity size={24} />;
    if (nameLower.includes('thyroid')) return <Wind size={24} />;
    if (nameLower.includes('lft') || nameLower.includes('liver')) return <PackageIcon size={24} />;
    if (nameLower.includes('full') || nameLower.includes('body')) return <Shield size={24} />;
    return <PackageIcon size={24} />;
  };

  // ✅ Handle Package Click
  const handlePackageClick = (pkg) => {
    setSelectedPackageId(pkg.packageId);
    setShowPackageDetails(true);
  };

  // ✅ Check if package is in cart
  const isPackageAdded = (packageId) => {
    return cart.some(
      (item) =>
        Number(item.productId) === Number(packageId) &&
        item.categoryId === 3
    );
  };

  // ✅ Updated handlePackageAction with login check
  const handlePackageAction = (pkg, e) => {
    e.stopPropagation();
    
    const userId = getUserId();
    
    if (!userId) {
      setShowLoginPopup(true);
      return;
    }
    
    const payload = {
      productId: pkg.packageId,
      categoryId: 3,
      price: Number(pkg.packagePrice) || 0,
      storeId: Number(pkg.labId) || 0,
      productName: pkg.packageName || "",
    };
    
    const added = isPackageAdded(pkg.packageId);
    if (added) {
      removeItem(payload);
    } else {
      addWithReplaceCheck(payload);
    }
  };

  // ✅ Get Package Rating
  const getPackageRating = (packageId) => {
    const key = `${packageId}_3`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  // ✅ Fetch ONLY Popular Packages - Direct API Call
  const fetchPopularPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API_BASE_URL}/MasterAPI/GetTestPackagesAll?pageNumber=1&pageSize=100`
      );
      
      let allPackages = [];
      if (response.data && response.data.succeeded) {
        const data = response.data.data || [];
        allPackages = Array.isArray(data) ? data : [];
      }
      
      // Sirf popular packages filter karo
      const popularPackages = allPackages.filter((pkg) => {
        return (
          (pkg.isActive === true || pkg.isActive === "true") &&
          (pkg.isPopular === true || pkg.isPopular === "true")
        );
      });
      
      setPackages(popularPackages);
      
      if (popularPackages.length > 0) {
        const ratingProducts = popularPackages.map(pkg => ({
          productId: pkg.packageId,
          categoryId: 3
        }));
        await dispatch(fetchAllRatings(ratingProducts));
      }
      
    } catch (err) {
      console.error('API Error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopularPackages();
    loadWishlist();
  }, []);

  // ✅ Render Stars
  const renderStars = (rating, size = 12) => {
    const stars = [];
    const roundedRating = Math.round(rating || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          size={size}
          className={`${
            i <= roundedRating
              ? 'text-yellow-500 fill-yellow-500'
              : 'text-gray-300'
          }`}
        />
      );
    }
    return stars;
  };

  // ✅ Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-red-500 mx-auto" />
          <p className="mt-4 text-gray-600">Loading popular packages...</p>
        </div>
      </div>
    );
  }

  // ✅ Error State
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h3>
          <p className="text-gray-600 text-sm">{error}</p>
          <button
            onClick={() => fetchPopularPackages()}
            className="mt-4 bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ✅ Main Render
  return (
    <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
      
      {showPackageDetails && (
        <GetPackageDetails
          id={selectedPackageId}
          onClose={() => setShowPackageDetails(false)}
        />
      )}

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">        
          <h1 className="text-3xl md:text-4xl font-extrabold">
            <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
              Popular
            </span>{" "}
            <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Health Packages
            </span>
          </h1>
          
          <p className="text-gray-500 text-sm mt-2 max-w-2xl mx-auto">
            Choose from our most popular health checkup packages trusted by thousands
          </p>
          
          <div className="mt-2 flex items-center justify-center gap-4 flex-wrap">
            <span className="text-xs text-gray-400">
              {packages.length} popular packages available
            </span>
          </div>
        </div>

        {packages.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold text-gray-700">No popular packages found</h3>
            <p className="text-gray-500 mt-2">Please check back later for popular packages</p>
          </div>
        )}

        {/* PACKAGES GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {packages.map((pkg) => {
            const { average: averageRating, total: totalReviews } = getPackageRating(pkg.packageId);
            
            const inCart = isPackageAdded(pkg.packageId);
            const inWishlist = isInWishlist(pkg.packageId, 3);
            const hasPopular = pkg.isPopular === true;
            const hasFasting = pkg.isFasting === true;
            const testCount = getTestsCount(pkg);
            
            // PRICE CALCULATION
            const finalPrice = Number(pkg.packagePrice) || 0;
            const originalPrice = Number(pkg.totalPrice) || 0;
            const discountAmount = Number(pkg.discountPrice) || 0;
            
            // Check if discount exists
            const hasDiscount = discountAmount > 0 && originalPrice > 0 && finalPrice < originalPrice;
            
            // Calculate discount percentage
            const discountPercent = originalPrice > 0 && discountAmount > 0 
              ? Math.round((discountAmount / originalPrice) * 100) 
              : 0;
            
            // ✅ Lab ID for Coupon
            const labIdForCoupon = pkg.labId || 0;
            
            return (
              <div
                key={pkg.packageId}
                className="group relative bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                onClick={() => handlePackageClick(pkg)}
              >
                {/* TOP ROW BADGES - Right side */}
                <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                  {hasPopular && (
                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
                      <Star size={10} fill="white" />
                      POPULAR
                    </div>
                  )}
                  
                  <button
                    onClick={(e) => toggleWishlist(pkg, e)}
                    className="p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
                    disabled={wishlistLoading}
                  >
                    {wishlistLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Heart 
                        size={14} 
                        className={`transition-colors duration-200 ${
                          inWishlist 
                            ? 'fill-red-500 text-red-500' 
                            : 'text-gray-400 hover:text-red-500'
                        }`}
                      />
                    )}
                  </button>
                </div>

                {/* TOP ROW BADGES - Left side */}
                <div className="absolute top-2 left-2 z-10 flex flex-col items-start gap-1">
                  {hasDiscount && discountAmount > 0 && (
                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-bold px-2 py-1 rounded-full shadow-md">
                      {discountPercent}% OFF
                    </div>
                  )}
                  
                  {hasFasting && (
                    <div className="bg-blue-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                      <Clock size={9} />
                      Fasting
                    </div>
                  )}
                </div>

                {/* Active Badge - Bottom Left */}
                <div className="absolute bottom-2 left-2 z-10">
                  <div className="bg-green-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full shadow-md flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                    ACTIVE
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-4 pt-12">
                  <div className="flex items-start justify-between mb-2">
                    <div className="p-2.5 bg-gradient-to-r from-red-50 to-red-100 rounded-xl group-hover:from-red-100 group-hover:to-red-200 transition-colors">
                      {getPackageIcon(pkg.packageName)}
                    </div>
                  </div>

                  <h3 className="text-sm font-bold text-gray-800 mb-1 line-clamp-1 group-hover:text-red-600 transition-colors">
                    {pkg.packageName}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2 line-clamp-2 min-h-[32px]">
                    {pkg.description || 'Comprehensive health package'}
                  </p>

                  {/* Rating Stars */}
                  <div className="flex items-center gap-1 mb-2">
                    <div className="flex">
                      {renderStars(averageRating, 11)}
                    </div>
                    {ratingLoading ? (
                      <span className="text-[9px] text-gray-400">Loading...</span>
                    ) : (
                      <span className="text-[9px] text-gray-500">
                        ({totalReviews})
                      </span>
                    )}
                  </div>

                  {/* Lab Name + Company Name + Test Count */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 text-[7px] font-bold">✓</span>
                      </div>
                      <span className="text-[9px] text-gray-600 truncate max-w-[80px]">{pkg.labName || 'N/A'}</span>
                    </div>
                    {pkg.companyName && (
                      <>
                        <span className="text-gray-300 text-[9px]">|</span>
                        <span className="text-[9px] text-gray-500 truncate max-w-[60px]">{pkg.companyName}</span>
                      </>
                    )}
                    <span className="text-[9px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full ml-auto">
                      {testCount} {testCount > 1 ? 'Tests' : 'Test'}
                    </span>
                  </div>

                  {/* ✅ PRICE SECTION WITH COUPON SUPPORT */}
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    {hasDiscount ? (
                      // ---------- PRICE SECTION (with discount) ----------
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-lg font-bold text-green-600">
                            ₹{finalPrice.toLocaleString()}
                          </span>
                          {originalPrice > 0 && (
                            <span className="text-xs line-through text-gray-400 ml-1.5">
                              ₹{originalPrice.toLocaleString()}
                            </span>
                          )}
                          {discountAmount > 0 && (
                            <span className="block text-[9px] text-red-500 font-semibold">
                              Save ₹{discountAmount.toLocaleString()} ({discountPercent}% OFF)
                            </span>
                          )}
                        </div>

                        {/* ✅ BIGGER Add to Cart Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePackageAction(pkg, e);
                          }}
                          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[11px] font-semibold border transition-all duration-200
                            ${
                              inCart
                                ? 'bg-red-100 text-red-600 border-red-400 hover:bg-red-200'
                                : 'bg-blue-100 text-blue-600 border-blue-400 hover:bg-blue-200'
                            }`}
                        >
                          <ShoppingBag size={14} />
                          {inCart ? 'Remove' : 'Add'}
                        </button>
                      </div>
                    ) : (
                      // ---------- COUPON SECTION (no discount) ----------
                      <div className="flex flex-col gap-1.5">
                        <div className="flex justify-between items-center">
                          <div>
                            {finalPrice > 0 && (
                              <span className="text-lg font-bold text-green-600">
                                ₹{finalPrice.toLocaleString()}
                              </span>
                            )}
                            {originalPrice > 0 && originalPrice !== finalPrice && (
                              <span className="text-xs line-through text-gray-400 ml-1.5">
                                ₹{originalPrice.toLocaleString()}
                              </span>
                            )}
                            {originalPrice === finalPrice && originalPrice > 0 && (
                              <span className="block text-[9px] text-gray-400 font-medium mt-0.5">
                                No Discount
                              </span>
                            )}
                            {originalPrice === 0 && finalPrice === 0 && (
                              <span className="block text-[9px] text-gray-400 font-medium mt-0.5">
                                Price not available
                              </span>
                            )}
                          </div>

                          {/* ✅ BIGGER Add to Cart Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePackageAction(pkg, e);
                            }}
                            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[11px] font-semibold border transition-all duration-200
                              ${
                                inCart
                                  ? 'bg-red-100 text-red-600 border-red-400 hover:bg-red-200'
                                  : 'bg-blue-100 text-blue-600 border-blue-400 hover:bg-blue-200'
                              }`}
                          >
                            <ShoppingBag size={14} />
                            {inCart ? 'Remove' : 'Add'}
                          </button>
                        </div>

                        {/* ✅ Coupon Component - Below Price & Add Button */}
                        <Coupon
                          labId={labIdForCoupon}
                          userId={getUserId()}
                          testPrice={originalPrice}
                          sellingPrice={finalPrice}
                          discount={0}
                          showCopyButton={false}
                          showAddToCart={false}
                          isInCart={inCart}
                          onAddToCart={() => handlePackageAction(pkg, { stopPropagation: () => {} })}
                          onRemoveFromCart={() => handlePackageAction(pkg, { stopPropagation: () => {} })}
                          className="w-full"
                          loadingText="Loading coupon..."
                          noCouponText="No coupon available"
                          labelText="🎫 No Discount - Coupon"
                        />
                      </div>
                    )}
                  </div>

                  {/* Bottom Info */}
                  <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-end">
                    <span className="text-[9px] text-green-600 font-medium">
                      ✅ Active
                    </span>
                  </div>
                </div>

                {/* Bottom Border Animation */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-red-500 to-transparent 
                transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </div>
            );
          })}
        </div>

        {/* Stats */}
        {packages.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Shield size={18} className="text-green-500" />
                <span>{packages.length} Popular Packages</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Star size={18} className="text-yellow-500" />
                <span>{packages.filter(p => p.isPopular).length} Featured</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Activity size={18} className="text-blue-500" />
                <span>Trusted Labs</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Heart size={18} className="text-red-500" />
                <span>Top Rated</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <ReplaceCartPopup />

      {/* ✅ Login Popup */}
      <LoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
      />
      
      {/* ✅ LoginPopupComponent from useCartActions */}
      {LoginPopupComponent}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}